import React from "react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select";
import { CheckCircle2 } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ScorecardField, ScorecardValue } from "@/types/scorecard";
import { getDropdownOptions, getRatingConfig, isFieldCompleted } from "./scorecardUtils";

interface ScorecardFieldInputProps {
	field: ScorecardField;
	value: ScorecardValue | undefined;
	onChange: (fieldId: string, value: ScorecardValue) => void;
	/** After a save attempt, highlights this field when required and empty. */
	showRequiredHint: boolean;
}

/** One scorecard criterion: label, guidance, and the type-specific input. */
export const ScorecardFieldInput: React.FC<ScorecardFieldInputProps> = React.memo(
	({ field, value, onChange, showRequiredHint }) => {
		const { t } = useTranslation();
		const completed = isFieldCompleted(field, value);
		const missingRequired = showRequiredHint && field.required && !completed;

		const renderInput = () => {
			switch (field.field_type) {
				case "text":
					return (
						<Input
							value={typeof value === "string" ? value : ""}
							onChange={(event) => onChange(field.id, event.target.value)}
							aria-label={field.name}
						/>
					);
				case "textarea":
					return (
						<Textarea
							value={typeof value === "string" ? value : ""}
							onChange={(event) => onChange(field.id, event.target.value)}
							rows={3}
							aria-label={field.name}
						/>
					);
				case "number":
					return (
						<Input
							type="number"
							value={typeof value === "number" ? value : ""}
							onChange={(event) => {
								const raw = event.target.value;
								onChange(field.id, raw === "" ? null : Number(raw));
							}}
							aria-label={field.name}
							className="max-w-[10rem]"
						/>
					);
				case "boolean":
					return (
						<div className="flex items-center gap-2">
							<Button
								type="button"
								size="sm"
								variant={value === true ? "default" : "outline"}
								aria-pressed={value === true}
								onClick={() => onChange(field.id, true)}
							>
								{t("candidates.tabsContent.scorecard.yes", "Yes")}
							</Button>
							<Button
								type="button"
								size="sm"
								variant={value === false ? "default" : "outline"}
								aria-pressed={value === false}
								onClick={() => onChange(field.id, false)}
							>
								{t("candidates.tabsContent.scorecard.no", "No")}
							</Button>
							{(value === true || value === false) && (
								<Button
									type="button"
									size="sm"
									variant="ghost"
									className="text-muted-foreground"
									onClick={() => onChange(field.id, null)}
								>
									{t("candidates.tabsContent.scorecard.clear", "Clear")}
								</Button>
							)}
						</div>
					);
				case "dropdown": {
					const options = getDropdownOptions(field);
					return (
						<Select
							value={typeof value === "string" && value !== "" ? value : undefined}
							onValueChange={(selected) => onChange(field.id, selected)}
						>
							<SelectTrigger className="max-w-xs" aria-label={field.name}>
								<SelectValue
									placeholder={t(
										"candidates.tabsContent.scorecard.selectPlaceholder",
										"Select an option",
									)}
								/>
							</SelectTrigger>
							<SelectContent>
								{options.map((option) => (
									<SelectItem key={option} value={option}>
										{option}
									</SelectItem>
								))}
							</SelectContent>
						</Select>
					);
				}
				case "rating": {
					const config = getRatingConfig(field);
					const steps = Array.from(
						{ length: config.max - config.min + 1 },
						(_, index) => config.min + index,
					);
					return (
						<div className="space-y-1">
							<div
								className="flex flex-wrap items-center gap-1.5"
								role="radiogroup"
								aria-label={field.name}
							>
								{steps.map((step) => (
									<button
										key={step}
										type="button"
										role="radio"
										aria-checked={value === step}
										onClick={() => onChange(field.id, value === step ? null : step)}
										className={cn(
											"h-8 min-w-8 rounded-sm border border-border px-2 text-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
											value === step
												? "border-primary bg-primary text-primary-foreground"
												: "bg-card text-foreground hover:bg-muted/60",
										)}
									>
										{step}
									</button>
								))}
							</div>
							{(config.minLabel || config.maxLabel) && (
								<div className="flex justify-between text-xs text-muted-foreground">
									<span>{config.minLabel}</span>
									<span>{config.maxLabel}</span>
								</div>
							)}
						</div>
					);
				}
				default:
					return null;
			}
		};

		return (
			<div
				className={cn(
					"space-y-1.5 rounded-sm border border-transparent p-2 -m-2",
					missingRequired && "border-amber-500/60 bg-amber-500/5",
				)}
			>
				<div className="flex items-center gap-1.5">
					<span className="text-sm font-medium text-foreground">{field.name}</span>
					{field.required && (
						<span
							className="text-destructive"
							title={t("candidates.tabsContent.scorecard.requiredField", "Required field")}
							aria-label={t("candidates.tabsContent.scorecard.requiredField", "Required field")}
						>
							*
						</span>
					)}
					{completed && (
						<CheckCircle2
							className="h-3.5 w-3.5 text-emerald-600 dark:text-emerald-500"
							aria-hidden="true"
						/>
					)}
				</div>
				{field.description && (
					<p className="text-xs text-muted-foreground">{field.description}</p>
				)}
				{renderInput()}
			</div>
		);
	},
);
ScorecardFieldInput.displayName = "ScorecardFieldInput";
